class CustomProductSizeCalculator extends HTMLElement {
    constructor() {
        super();

        this.initialized = false;
        this.handleInput = this.handleInput.bind(this);
        this.handleChange = this.handleChange.bind(this);
        this.handleFormSubmit = this.handleFormSubmit.bind(this);
        this.handleDimensionInputFocus = this.handleDimensionInputFocus.bind(this);
        this.handleDimensionInputMouseUp = this.handleDimensionInputMouseUp.bind(this);
    }

    connectedCallback() {
        if (this.initialized) return;

        this.initialized = true;
        this.productRoot = this.closest('[data-product-main]') || document;
        this.widthInput = this.querySelector('[data-custom-size-width]');
        this.heightInput = this.querySelector('[data-custom-size-height]');
        this.summaryEl = this.querySelector('[data-custom-size-summary]');
        this.priceEl = this.querySelector('[data-custom-size-price]');
        this.errorEl = this.querySelector('[data-custom-size-error]');
        this.form = this.productRoot.querySelector('form[data-custom-add-to-cart]');
        this.mainPriceEl = this.productRoot.querySelector('.product-price-wrapper span[data-product-price]');

        this.basePrice = this.normalizeMoneyValue(this.dataset.basePrice);
        this.areaMultiplier = this.normalizeMoneyValue(this.dataset.areaMultiplier);
        this.minPriceCents = this.toNumber(this.dataset.minPriceCents, 0);
        this.minWidth = this.toNumber(this.dataset.minWidth, 0);
        this.maxWidth = this.toNumber(this.dataset.maxWidth, 0);
        this.minHeight = this.toNumber(this.dataset.minHeight, 0);
        this.maxHeight = this.toNumber(this.dataset.maxHeight, 0);
        this.increment = Math.max(this.toNumber(this.dataset.increment, 1), 1);

        this.widthInput?.addEventListener('input', this.handleInput);
        this.widthInput?.addEventListener('change', this.handleChange);
        this.widthInput?.addEventListener('focus', this.handleDimensionInputFocus);
        this.widthInput?.addEventListener('mouseup', this.handleDimensionInputMouseUp);
        this.heightInput?.addEventListener('input', this.handleInput);
        this.heightInput?.addEventListener('change', this.handleChange);
        this.heightInput?.addEventListener('focus', this.handleDimensionInputFocus);
        this.heightInput?.addEventListener('mouseup', this.handleDimensionInputMouseUp);
        this.form?.addEventListener('submit', this.handleFormSubmit, true);

        this.update({ commitRoundedValues: true });
    }

    disconnectedCallback() {
        this.widthInput?.removeEventListener('input', this.handleInput);
        this.widthInput?.removeEventListener('change', this.handleChange);
        this.widthInput?.removeEventListener('focus', this.handleDimensionInputFocus);
        this.widthInput?.removeEventListener('mouseup', this.handleDimensionInputMouseUp);
        this.heightInput?.removeEventListener('input', this.handleInput);
        this.heightInput?.removeEventListener('change', this.handleChange);
        this.heightInput?.removeEventListener('focus', this.handleDimensionInputFocus);
        this.heightInput?.removeEventListener('mouseup', this.handleDimensionInputMouseUp);
        this.form?.removeEventListener('submit', this.handleFormSubmit, true);
    }

    handleInput() {
        this.update();
    }

    handleChange() {
        this.update({ commitRoundedValues: true });
    }

    handleFormSubmit(event) {
        const result = this.update({ commitRoundedValues: true });
        if (result.valid) return;

        event.preventDefault();
        event.stopImmediatePropagation();
        this.focusFirstInvalidInput();
    }

    handleDimensionInputFocus(event) {
        this.selectDimensionInput(event.currentTarget);
    }

    handleDimensionInputMouseUp(event) {
        event.preventDefault();
        this.selectDimensionInput(event.currentTarget);
    }

    selectDimensionInput(input) {
        if (!input || typeof input.select !== 'function') return;

        requestAnimationFrame(() => {
            input.select();
        });
    }

    update({ commitRoundedValues = false } = {}) {
        const width = this.normalizeDimension(
            this.getInputNumber(this.widthInput),
            this.minWidth,
            this.maxWidth
        );
        const height = this.normalizeDimension(
            this.getInputNumber(this.heightInput),
            this.minHeight,
            this.maxHeight
        );
        const validation = this.validateDimensions(width, height);

        this.widthInput?.setAttribute('aria-invalid', validation.invalidFields.has('width') ? 'true' : 'false');
        this.heightInput?.setAttribute('aria-invalid', validation.invalidFields.has('height') ? 'true' : 'false');

        if (!validation.valid) {
            this.setError(validation.message);
            this.setAddToCartDisabled(true);
            this.updateHiddenProperties(null);
            if (this.priceEl) this.priceEl.textContent = '-';
            this.updateMainPrice('-');
            return { valid: false };
        }

        const priceCents = this.calculatePriceCents(width, height);
        const formattedPrice = this.formatMoney(priceCents);

        if (commitRoundedValues) {
            this.setInputNumber(this.widthInput, width);
            this.setInputNumber(this.heightInput, height);
        }

        this.clearError();
        this.setAddToCartDisabled(false);
        this.updateSummary(width, height);
        this.updateHiddenProperties({
            width,
            height,
            priceCents,
            formattedPrice,
        });

        if (this.priceEl) this.priceEl.textContent = formattedPrice;
        this.updateMainPrice(formattedPrice);

        return {
            valid: true,
            width,
            height,
            priceCents,
            formattedPrice,
        };
    }

    normalizeDimension(value, min, max) {
        if (!Number.isFinite(value) || value <= 0) return NaN;

        let normalized = value;

        if (min > 0 && normalized < min) return min;
        if (max > 0 && normalized > max) return max;

        normalized = Math.round(normalized / this.increment) * this.increment;

        if (min > 0 && normalized < min) normalized = min;
        if (max > 0 && normalized > max) normalized = max;

        return Math.round(normalized);
    }

    validateDimensions(width, height) {
        const invalidFields = new Set();

        if (!Number.isFinite(width) || width <= 0) invalidFields.add('width');
        if (!Number.isFinite(height) || height <= 0) invalidFields.add('height');

        if (invalidFields.size) {
            return {
                valid: false,
                invalidFields,
                message: 'Enter a valid width and height.',
            };
        }

        if (this.minWidth > 0 && width < this.minWidth) invalidFields.add('width');
        if (this.maxWidth > 0 && width > this.maxWidth) invalidFields.add('width');
        if (this.minHeight > 0 && height < this.minHeight) invalidFields.add('height');
        if (this.maxHeight > 0 && height > this.maxHeight) invalidFields.add('height');

        if (invalidFields.size) {
            return {
                valid: false,
                invalidFields,
                message: 'Enter a size within the available range.',
            };
        }

        if (!this.isValidIncrement(width)) invalidFields.add('width');
        if (!this.isValidIncrement(height)) invalidFields.add('height');

        if (invalidFields.size) {
            return {
                valid: false,
                invalidFields,
                message: `Sizes must be entered in ${this.increment}mm increments.`,
            };
        }

        return {
            valid: true,
            invalidFields,
            message: '',
        };
    }

    calculatePriceCents(width, height) {
        const areaSquareMetres = (width * height) / 1000000;
        const calculatedPrice = this.basePrice + (areaSquareMetres * this.areaMultiplier);
        const minPrice = this.minPriceCents / 100;
        const finalPrice = this.roundUpToEndingNine(Math.max(calculatedPrice, minPrice));

        return Math.round(finalPrice * 100);
    }

    roundUpToEndingNine(value) {
        const roundedInteger = Math.ceil(this.toNumber(value, 0));
        const remainder = roundedInteger % 10;
        if (remainder === 9) return roundedInteger;

        return roundedInteger + ((9 - remainder + 10) % 10);
    }

    isValidIncrement(value) {
        const roundedValue = Math.round(value);
        if (Math.abs(value - roundedValue) > 0.0001) return false;

        return roundedValue % this.increment === 0;
    }

    getInputNumber(input) {
        if (!input) return NaN;
        if (String(input.value || '').trim() === '') return NaN;
        return this.toNumber(input.value, NaN);
    }

    setInputNumber(input, value) {
        if (!input || !Number.isFinite(value)) return;

        input.value = String(value);
    }

    toNumber(value, fallback) {
        const number = Number(value);
        return Number.isFinite(number) ? number : fallback;
    }

    normalizeMoneyValue(value) {
        return this.toNumber(value, 0);
    }

    updateSummary(width, height) {
        if (!this.summaryEl) return;
        this.summaryEl.textContent = `Entered size: ${width}mm x ${height}mm`;
    }

    updateHiddenProperties(data) {
        if (!data) {
            this.setPropertyValue('width_raw', '');
            this.setPropertyValue('height_raw', '');
            this.setPropertyValue('price_cents', '');
            return;
        }

        this.setPropertyValue('width_raw', String(data.width));
        this.setPropertyValue('height_raw', String(data.height));
        this.setPropertyValue('price_cents', String(data.priceCents));
    }

    setPropertyValue(propertyName, value) {
        const input = this.productRoot.querySelector(`[data-custom-size-property="${propertyName}"]`);
        if (input) input.value = value;
    }

    updateMainPrice(value) {
        if (!this.mainPriceEl) return;

        this.mainPriceEl.textContent = value;
    }

    setAddToCartDisabled(disabled) {
        this.productRoot.querySelectorAll('[data-custom-add-to-cart-button], [data-sticky-atc-button]').forEach(button => {
            if (!button.dataset.customSizeInitiallyDisabled) {
                button.dataset.customSizeInitiallyDisabled = button.hasAttribute('disabled') ? 'true' : 'false';
            }

            if (disabled) {
                button.dataset.customSizeDisabled = 'true';
                button.setAttribute('disabled', 'true');
                return;
            }

            if (
                button.dataset.customSizeDisabled === 'true'
                && button.dataset.customSizeInitiallyDisabled !== 'true'
            ) {
                button.removeAttribute('disabled');
            }

            delete button.dataset.customSizeDisabled;
        });
    }

    setError(message) {
        this.classList.add('has-error');
        if (!this.errorEl) return;

        this.errorEl.textContent = message || '';
        this.errorEl.hidden = false;
    }

    clearError() {
        this.classList.remove('has-error');
        if (!this.errorEl) return;

        this.errorEl.textContent = '';
        this.errorEl.hidden = true;
    }

    focusFirstInvalidInput() {
        const input = this.querySelector('[aria-invalid="true"]');
        if (input && typeof input.focus === 'function') {
            input.focus();
        }
    }

    formatMoney(cents) {
        const centsNumber = Number(cents || 0);
        const isNegative = centsNumber < 0;
        const absoluteCents = Math.abs(centsNumber);
        let formattedMoney = '';

        if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
            formattedMoney = window.Shopify.formatMoney(absoluteCents);
        } else {
            formattedMoney = `$${(absoluteCents / 100).toLocaleString('en-AU', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
            })}`;
        }

        return isNegative ? `-${formattedMoney}` : formattedMoney;
    }
}

if (!customElements.get('custom-product-size-calculator')) {
    customElements.define('custom-product-size-calculator', CustomProductSizeCalculator);
}
